/**
 * 오피스 편집기 AI — **호스트 쪽 에이전트 루프** (2026-08-15, PR5-A).
 *
 * ## 왜 필요한가
 *
 * GenOffice 다섯(md·Word·PDF·Excel·PPT)은 루프(`agent-core`)가 **편집기 번들 안에** 있어서
 * 도구를 부르고 결과를 되먹인다. 한글(HWP)·평문은 그 번들이 없다 — 호스트가 그린 카드가
 * `startOfficeAiTurn` 을 **한 번** 부르고 끝냈다(`onToolCall: () => {}`). 그래서 서버가 도구
 * 호출을 제대로 내려보내도 받는 쪽이 버려, 두 편집기는 구조상 "제안만" 할 수 있었다.
 *
 * 이 모듈이 그 빈자리다. 서버 계약(한 턴 = 요청 하나)은 그대로 두고, **턴을 잇는 일**만 한다.
 *
 * ## 상류 `agent-core` 를 가져오지 않는 이유
 *
 * 그쪽 루프는 Electron 트랜스포트·스킬 합성·압축 턴까지 얽혀 있어 브라우저 호스트로
 * 끌어오면 벤더링 소스를 통째로 지고 가야 한다. 우리에게 필요한 것은 네 가지뿐이다 —
 * 턴 반복, 도구 실행, 되먹임, 멈출 줄 아는 것.
 *
 * ## 서버와 맞춰야 하는 계약 (어기면 조용히 깨진다)
 *
 * - **도구 호출이 없는 턴 = 정상 종료.** 오류가 아니다. 여기서 실패로 처리하면 "AI 가
 *   답만 하면 오류" 가 된다(`office-ai-encode.ts` 의 같은 계약).
 * - **`inputError` 가 붙은 호출은 실행하지 않는다.** 그 문구를 도구 결과로 되돌려 모델이
 *   인자를 고쳐 다시 부르게 한다. 서버가 그러라고 만들어 보낸 것이다.
 * - **메시지 모양은 서버의 `AgentMessage` 와 같아야 한다** — assistant 턴은 `toolCalls`,
 *   도구 결과는 `{role:'tool', results:[…]}`. 어긋나면 전사 렌더링이 조용히 비어 버린다.
 * - **취소 뒤에도 한 번은 끝난다.** `startOfficeAiTurn` 이 `done` 을 보장하므로 그것을 믿되,
 *   루프 자신도 중단 신호를 매 턴 경계에서 확인한다.
 */

import {
  cancelOfficeAiTurn,
  startOfficeAiTurn,
  type OfficeAiToolCall,
  type OfficeAiTurnCallbacks,
  type OfficeAiTurnRequest,
} from './office-ai';

/** 호스트가 직접 실행하는 도구. 스키마는 서버가 모델에게 그대로 보여 준다. */
export interface HostAgentTool {
  name: string;
  description: string;
  inputSchema: Record<string, unknown>;
  execute(input: Record<string, unknown>): HostToolResult | Promise<HostToolResult>;
}

export interface HostToolResult {
  /** 모델에게 돌려줄 글. 실패도 글로 돌려준다 — 그래야 다음 턴에 고칠 수 있다. */
  output: string;
  isError?: boolean;
  /** 사람에게 보일 한 줄. 없으면 도구 이름으로 대신한다. */
  summary?: string;
  /**
   * 문서를 실제로 바꿨는가 (2026-08-15, Phase C).
   *
   * 카드가 **되돌리기 단계 수**를 이것으로 센다 — 편집기가 자기 되돌리기 스택을 쥔 경우
   * (한글) 한 답변이 몇 단계였는지는 도구만 안다. 전선으로는 나가지 않는다.
   */
  mutated?: boolean;
}

/** 서버 계약과 같은 모양(`office-ai-encode.ts` 의 `AgentMessage`). */
export type AgentMessage =
  | { role: 'user'; text: string }
  | { role: 'assistant'; text: string; toolCalls?: Array<{ id: string; name: string; input: Record<string, unknown> }> }
  | { role: 'tool'; results: Array<{ id: string; name: string; output: string; isError?: boolean }> };

export interface AgentRunEvents {
  /** 이 턴의 산문이 확정됐다(스트리밍이 아니라 턴 단위 — 서버가 한 번에 준다). */
  onText?(text: string, turnIndex: number): void;
  /** 도구를 부르기 직전. 사람에게 "도는 중" 을 보여 줄 자리. */
  onToolStart?(call: OfficeAiToolCall): void;
  /** 도구가 끝났다. `result.isError` 면 실패로 그린다. */
  onToolResult?(call: OfficeAiToolCall, result: HostToolResult): void;
}

export interface AgentRunOptions extends AgentRunEvents {
  runId: string;
  editorKind: OfficeAiTurnRequest['editorKind'];
  fileId?: string;
  system: string;
  /** 첫 사용자 메시지까지 담은 전사. 루프가 여기에 이어 붙인다. */
  messages: AgentMessage[];
  tools: HostAgentTool[];
  /** 기본 8 — 상류 루프(`agent-core/src/loop.ts`)와 같은 값. */
  maxTurns?: number;
  /** 도구 출력 1건의 글자 상한. 서버도 다시 줄이지만 대역폭은 여기서 아낀다. */
  toolOutputMaxChars?: number;
  signal?: AbortSignal;
  transport?: TurnTransport;
  newRequestId?: () => string;
}

export interface AgentRunResult {
  /** 마지막 턴의 산문 — 사용자에게 보일 답. */
  text: string;
  /** 늘어난 전사(호출자가 다음 질문에 이어 쓴다). */
  messages: AgentMessage[];
  turns: number;
  /** 왜 끝났는가. `done` 만 정상이다. */
  stop: 'done' | 'max-turns' | 'cancelled' | 'error';
  error?: string;
}

/** 전송 계층 — 테스트가 갈아 끼운다. */
export interface TurnTransport {
  start(req: OfficeAiTurnRequest, cb: OfficeAiTurnCallbacks): Promise<void>;
  cancel(requestId: string): void;
}

const DEFAULT_TRANSPORT: TurnTransport = { start: startOfficeAiTurn, cancel: cancelOfficeAiTurn };

const DEFAULT_MAX_TURNS = 8;
const DEFAULT_TOOL_OUTPUT_MAX = 4_000;

/**
 * 같은 도구가 인자를 못 고치고 되풀이할 때의 상한.
 *
 * 서버가 `inputError` 를 붙여 보내면 모델이 고쳐 부르는데, 못 고치면 그대로 맴돈다.
 * `maxTurns` 로도 언젠가 끝나지만 그때까지 CLI 를 8번 스폰한다(한 번에 수십 초).
 */
const MAX_INPUT_ERROR_RETRIES = 3;

export async function runOfficeAiAgent(opts: AgentRunOptions): Promise<AgentRunResult> {
  const transport = opts.transport ?? DEFAULT_TRANSPORT;
  const maxTurns = opts.maxTurns ?? DEFAULT_MAX_TURNS;
  const outputMax = opts.toolOutputMaxChars ?? DEFAULT_TOOL_OUTPUT_MAX;
  const newRequestId = opts.newRequestId ?? newId;
  const toolsByName = new Map(opts.tools.map((t) => [t.name, t]));
  const wire = opts.tools.map((t) => ({
    name: t.name,
    description: t.description,
    inputSchema: t.inputSchema,
  }));

  const messages: AgentMessage[] = [...opts.messages];
  let text = '';
  let inputErrorRuns = 0;

  for (let turn = 0; turn < maxTurns; turn += 1) {
    if (opts.signal?.aborted) return { text, messages, turns: turn, stop: 'cancelled' };

    const requestId = newRequestId();
    const onAbort = (): void => transport.cancel(requestId);
    opts.signal?.addEventListener('abort', onAbort);

    let turnResult: TurnOutcome;
    try {
      turnResult = await runTurn(transport, {
        requestId,
        runId: opts.runId,
        editorKind: opts.editorKind,
        ...(opts.fileId ? { fileId: opts.fileId } : {}),
        system: opts.system,
        // ⚠ **복사해서 넘긴다.** 이 배열은 턴이 끝난 뒤 루프가 계속 이어 붙이는 것이라,
        //   참조로 넘기면 이미 보낸 요청 객체가 나중 상태를 비춘다. 실제 전송은 곧바로
        //   JSON 으로 굳어서 전선에는 문제가 없지만, 요청을 나중에 들여다보는 쪽
        //   (감사·재시도·테스트)이 있지도 않았던 대화를 본다.
        messages: [...messages],
        tools: wire,
      });
    } catch (err) {
      opts.signal?.removeEventListener('abort', onAbort);
      return { text, messages, turns: turn + 1, stop: 'error', error: errorText(err) };
    } finally {
      opts.signal?.removeEventListener('abort', onAbort);
    }

    if (turnResult.error) {
      // 취소는 실패가 아니다 — 계약상 취소도 `done` 으로 오지만, 신호가 먼저 서면 그쪽을 믿는다.
      if (opts.signal?.aborted) return { text, messages, turns: turn + 1, stop: 'cancelled' };
      return { text, messages, turns: turn + 1, stop: 'error', error: turnResult.error };
    }

    text = turnResult.text;
    if (turnResult.text) opts.onText?.(turnResult.text, turn);

    // ★ 도구 호출이 없는 턴 = 정상 종료. 루프는 여기서만 성공으로 끝난다.
    if (!turnResult.calls.length) {
      messages.push({ role: 'assistant', text: turnResult.text });
      return { text, messages, turns: turn + 1, stop: 'done' };
    }

    messages.push({
      role: 'assistant',
      text: turnResult.text,
      toolCalls: turnResult.calls.map((c) => ({ id: c.id, name: c.name, input: c.input })),
    });

    const results: Array<{ id: string; name: string; output: string; isError?: boolean }> = [];
    let sawInputError = false;

    for (const call of turnResult.calls) {
      if (opts.signal?.aborted) return { text, messages, turns: turn + 1, stop: 'cancelled' };

      // 인자가 깨진 호출은 **실행하지 않는다** — 문구를 그대로 되돌려 모델이 고치게 한다.
      if (call.inputError) {
        sawInputError = true;
        const result: HostToolResult = { output: call.inputError, isError: true };
        opts.onToolResult?.(call, result);
        results.push({ id: call.id, name: call.name, output: call.inputError, isError: true });
        continue;
      }

      const tool = toolsByName.get(call.name);
      if (!tool) {
        // 서버가 걸러 주지만(unknown tool → inputError) 이중 방어다. 목록을 함께 줘야
        // 모델이 다음 턴에 고를 수 있다.
        const output = `unknown tool "${call.name}"; use one of: ${[...toolsByName.keys()].join(', ')}`;
        opts.onToolResult?.(call, { output, isError: true });
        results.push({ id: call.id, name: call.name, output, isError: true });
        continue;
      }

      opts.onToolStart?.(call);
      let result: HostToolResult;
      try {
        result = await tool.execute(call.input);
      } catch (err) {
        // ⚠ 도구가 던져도 **대화를 끝내지 않는다.** 실패를 글로 돌려주면 모델이 다른 방법을
        //   찾거나 사용자에게 설명한다. 여기서 throw 하면 사용자는 이유 없이 멈춘 화면을 본다.
        result = { output: `tool "${call.name}" failed: ${errorText(err)}`, isError: true };
      }
      const output = clip(result.output, outputMax);
      opts.onToolResult?.(call, { ...result, output });
      results.push({ id: call.id, name: call.name, output, ...(result.isError ? { isError: true } : {}) });
    }

    messages.push({ role: 'tool', results });

    inputErrorRuns = sawInputError ? inputErrorRuns + 1 : 0;
    if (inputErrorRuns >= MAX_INPUT_ERROR_RETRIES) {
      return {
        text,
        messages,
        turns: turn + 1,
        stop: 'error',
        error: '도구 인자를 계속 잘못 만들어 실행을 멈췄습니다. 요청을 더 작게 나눠 다시 시도해 주세요.',
      };
    }
  }

  return { text, messages, turns: maxTurns, stop: 'max-turns' };
}

interface TurnOutcome {
  text: string;
  calls: OfficeAiToolCall[];
  error?: string;
}

/** 한 턴 — 스트림이 끝날 때까지 모아서 돌려준다. */
function runTurn(transport: TurnTransport, req: OfficeAiTurnRequest): Promise<TurnOutcome> {
  return new Promise<TurnOutcome>((resolve, reject) => {
    let text = '';
    const calls: OfficeAiToolCall[] = [];
    let settled = false;
    const settle = (out: TurnOutcome): void => {
      if (settled) return;
      settled = true;
      resolve(out);
    };

    transport
      .start(req, {
        onPing: () => {},
        onDelta: (chunk) => {
          text += chunk;
        },
        onToolCall: (call) => {
          calls.push(call);
        },
        onDone: () => settle({ text: text.trim(), calls }),
        onError: (message) => settle({ text: text.trim(), calls, error: message }),
      })
      .catch((err: unknown) => {
        if (!settled) {
          settled = true;
          reject(err instanceof Error ? err : new Error(errorText(err)));
        }
      });
  });
}

function clip(s: string, max: number): string {
  return s.length <= max ? s : `${s.slice(0, max)}\n…(생략됨)`;
}

function errorText(err: unknown): string {
  const message = (err as Error)?.message;
  return typeof message === 'string' && message ? message : 'AI 실행에 실패했습니다.';
}

function newId(): string {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') return crypto.randomUUID();
  return `wsai-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`;
}
